import { useEffect, useMemo, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Upload, Trash2, Tag, Sparkles, Download, Pencil, Search, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", company_name: "", email: "", phone: "", csi_codes: [] };

export default function Subcontractors() {
  const [subs, setSubs] = useState([]);
  const [csi, setCsi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  const fileRef = useRef();

  const load = () => Promise.all([api.get("/subcontractors"), api.get("/csi-codes")])
    .then(([s, c]) => { setSubs(s.data); setCsi(c.data); });
  useEffect(() => { load(); }, []);

  const codeTitle = useMemo(() => Object.fromEntries(csi.map((c) => [c.code, c.title])), [csi]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) => [s.name, s.company_name, s.email, s.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
  }, [subs, search]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setCodeFilter(""); setOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name || "", company_name: s.company_name || "", email: s.email || "", phone: s.phone || "", csi_codes: s.csi_codes || [] });
    setCodeFilter(""); setOpen(true);
  };

  const toggleCode = (code) =>
    setForm((f) => ({ ...f, csi_codes: f.csi_codes.includes(code) ? f.csi_codes.filter((x) => x !== code) : [...f.csi_codes, code] }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Contact name and email are required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/subcontractors/${editingId}`, form);
        toast.success("Subcontractor updated");
      } else {
        await api.post("/subcontractors", form);
        toast.success("Subcontractor added");
      }
      setOpen(false); setForm(emptyForm); setEditingId(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}${s.company_name ? ` (${s.company_name})` : ""} from your directory?`)) return;
    setDeletingId(s.id);
    try {
      await api.delete(`/subcontractors/${s.id}`);
      toast.success("Subcontractor removed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeletingId(null); }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/subcontractors/seed-demo");
      toast.success(data.inserted ? `Added ${data.inserted} sample subcontractors` : "Sample subs already added");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const downloadSampleCsv = () => {
    const rows = [
      ["name", "company_name", "email", "phone", "csi_codes"],
      ["Mike Reynolds", "Reynolds Electric Co.", "mike@reynoldselectric.com", "555-0101", "Electrical; 27"],
      ["Tom Baker", "Baker Mechanical", "tom@bakermech.com", "555-0103", "Plumbing; HVAC"],
      ["Carlos Diaz", "Diaz Concrete Works", "carlos@diazconcrete.com", "555-0105", "03"],
      ["Joe Martin", "Martin Masonry", "joe@martinmasonry.com", "555-0116", "04 00 00"],
    ];
    const csv = rows.map((r) => r.map((c) => (/[",;\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bidrouter-subcontractors-sample.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Sample CSV downloaded");
  };

  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/subcontractors/import", fd);
      let msg = `Imported ${data.inserted} subcontractors`;
      if (data.unmatched_codes?.length) msg += ` · ${data.unmatched_codes.length} code(s) not recognized: ${data.unmatched_codes.slice(0, 5).join(", ")}`;
      if (data.unmatched_codes?.length) toast.warning(msg); else toast.success(msg);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const filteredCodes = csi.filter((c) => {
    const q = codeFilter.trim().toLowerCase();
    return !q || c.code.toLowerCase().includes(q) || (c.title || "").toLowerCase().includes(q);
  });

  return (
    <>
      <PageHeader title="Subcontractor Directory" description="Your company's vendor list, tagged by CSI code"
        actions={
          <div className="flex flex-wrap gap-2">
            <input type="file" accept=".csv" ref={fileRef} onChange={importCsv} className="hidden" data-testid="csv-input" />
            <Button variant="ghost" onClick={seedDemo} disabled={seeding} data-testid="seed-demo-btn">{seeding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{seeding ? "Adding…" : "Add samples"}</Button>
            <Button variant="ghost" onClick={downloadSampleCsv} data-testid="download-sample-csv-btn"><Download className="h-4 w-4 mr-1" />Sample CSV</Button>
            <Button variant="outline" onClick={() => fileRef.current.click()} disabled={importing} data-testid="import-csv-btn">{importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{importing ? "Importing…" : "Import CSV"}</Button>
            <Button onClick={openAdd} data-testid="add-sub-btn"><Plus className="h-4 w-4 mr-1" />Add sub</Button>
          </div>
        } />

      <div className="p-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, email or phone…" className="pl-9" data-testid="sub-search" />
          </div>
          <p className="text-sm text-muted-foreground">{filtered.length} of {subs.length} subcontractor{subs.length === 1 ? "" : "s"}</p>
        </div>

        <Card className="border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="py-3">Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>CSI Codes</TableHead>
                <TableHead className="text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-14">
                  {subs.length === 0 ? "No subcontractors yet — add one or import a CSV to get started." : "No matches for your search."}
                </TableCell></TableRow>
              )}
              {filtered.map((s) => (
                <TableRow key={s.id} className="hover:bg-accent/40 border-b border-border/60" data-testid={`sub-row-${s.id}`}>
                  <TableCell className="py-3">
                    <div className="font-semibold text-foreground leading-tight">{s.name}</div>
                    {s.company_name && <div className="text-xs text-muted-foreground mt-0.5">{s.company_name}</div>}
                  </TableCell>
                  <TableCell>
                    {s.email ? <a href={`mailto:${s.email}`} className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid={`sub-email-${s.id}`}><Mail className="h-3.5 w-3.5 opacity-70" />{s.email}</a> : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell>
                    {s.phone ? <span className="text-sm text-foreground inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{s.phone}</span> : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell>
                    {s.csi_codes?.length ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Badge variant="outline" className="cursor-pointer border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 transition-colors" data-testid={`sub-codes-${s.id}`}><Tag className="h-3 w-3 mr-1" />{s.csi_codes.length} code{s.csi_codes.length === 1 ? "" : "s"}</Badge>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 max-h-64 overflow-y-auto text-sm p-2 space-y-1">
                          {s.csi_codes.map((c) => (
                            <div key={c} className="flex items-baseline gap-2 px-1 py-0.5">
                              <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded shrink-0">{c}</span>
                              <span className="text-muted-foreground text-xs">{codeTitle[c] || ""}</span>
                            </div>
                          ))}
                        </PopoverContent>
                      </Popover>
                    ) : <span className="text-muted-foreground text-sm">No codes</span>}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-sub-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={() => remove(s)} disabled={deletingId === s.id} data-testid={`delete-sub-${s.id}`}>
                        {deletingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Importing a CSV?</span> Columns: <span className="font-mono">name, company_name, email, phone, csi_codes</span>. Put multiple codes in one cell separated by <span className="font-mono">;</span> — loose entries like <span className="font-mono">26</span>, <span className="font-mono">Electrical</span>, or <span className="font-mono">Div 26</span> are auto-mapped. Use <span className="font-medium">Sample CSV</span> for a template.
        </p>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent data-testid="sub-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit subcontractor" : "Add subcontractor"}</DialogTitle>
            <DialogDescription>{editingId ? "Update this vendor's contact details or the CSI codes they bid on." : "Add a vendor and tag them with the CSI codes they bid on."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Contact name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="sub-name" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Company</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5" data-testid="sub-company" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="sub-email" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="sub-phone" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">CSI codes</Label>
                <span className="text-xs text-primary font-medium">{form.csi_codes.length} selected</span>
              </div>
              <div className="relative mb-2">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)} placeholder="Filter codes…" className="pl-8 h-9 text-sm" data-testid="sub-code-filter" />
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-md">
                {filteredCodes.length === 0 && <p className="text-sm text-muted-foreground px-3 py-4 text-center">No codes match.</p>}
                {filteredCodes.map((c) => (
                  <label key={c.code} className="flex items-center gap-2.5 px-3 py-2 border-b border-border/60 last:border-0 hover:bg-accent cursor-pointer text-sm">
                    <Checkbox checked={form.csi_codes.includes(c.code)} onCheckedChange={() => toggleCode(c.code)} data-testid={`sub-csi-${c.code.replace(/ /g, '')}`} />
                    <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{c.code}</span>
                    <span className="text-muted-foreground">{c.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="cancel-sub-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-sub-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {saving ? "Saving…" : editingId ? "Save changes" : "Add subcontractor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
